import type { Representada } from '../db/tipos'
import { normalizarTexto } from '../lib/textoBR'

// Forma de pagamento = COMO o cliente paga (spec 27/09, Parte 3). Nao e prazo:
// prazo e a condicao. As 6 fixas moram aqui, e o banco guarda so os NOMES:
// formas_pagamento_aceitas = as ativas; formas_pagamento_extras = as do [+].
export const FORMAS_FIXAS: readonly string[] = ['PIX', 'Boleto', 'Cartão de crédito', 'Dinheiro', 'Transferência', 'Antecipado']

export const MSG_FORMA_VAZIA = 'Informe o nome da forma de pagamento.'
export const MSG_FORMA_REPETIDA = 'Essa forma já está na lista.'
export const MSG_FORMA_PENDENTE = 'Você digitou uma forma de pagamento e não adicionou. Toque em Adicionar ou apague o texto.'

export interface FormaNaTela { nome: string; ativa: boolean; fixa: boolean }

const chave = (nome: string) => normalizarTexto(nome).trim()

export function formasNaTela(ativas: readonly string[], extras: readonly string[]): FormaNaTela[] {
  const ativasN = new Set(ativas.map(chave))
  const fixasN = new Set(FORMAS_FIXAS.map(chave))
  const vistas = new Set<string>()
  const outras: FormaNaTela[] = []
  // `ativas` tambem entra: uma forma digitada no campo de texto livre de antes
  // (nenhuma das 39 em 27/09, mas o sync pode trazer) nao pode sumir da tela.
  for (const nome of [...extras, ...ativas]) {
    const n = chave(nome)
    if (fixasN.has(n) || vistas.has(n)) continue
    vistas.add(n)
    outras.push({ nome: nome.trim(), ativa: ativasN.has(n), fixa: false })
  }
  return [...FORMAS_FIXAS.map((nome) => ({ nome, ativa: ativasN.has(chave(nome)), fixa: true })), ...outras]
}

// Finding 2 da revisao final: uma forma LEGADA de texto livre (nome fora das
// fixas e fora de extras -- o sync antigo podia trazer isso, formasNaTela
// comentario acima) so aparecia na tela ENQUANTO ativa. Ao inativar, ela
// sumia por completo -- nada em `ativas` (perdeu a marca) nem em `extras`
// (nunca esteve la). Por isso precisa de `extras` aqui: inativar um nome
// assim tem de acrescenta-lo a extras, para continuar visivel como Inativa.
export function alternarForma(
  ativas: readonly string[], extras: readonly string[], nome: string,
): { formasAtivas: string[]; formasExtras: string[] } {
  const n = chave(nome)
  const fixasN = new Set(FORMAS_FIXAS.map(chave))
  if (ativas.some((a) => chave(a) === n)) {
    const jaEmExtras = extras.some((e) => chave(e) === n)
    const formasExtras = fixasN.has(n) || jaEmExtras ? [...extras] : [...extras, nome]
    return { formasAtivas: ativas.filter((a) => chave(a) !== n), formasExtras }
  }
  return { formasAtivas: [...ativas, nome], formasExtras: [...extras] }
}

export function adicionarFormaExtra(
  ativas: readonly string[], extras: readonly string[], nome: string,
): { ok: true; formasAtivas: string[]; formasExtras: string[] } | { ok: false; erro: string } {
  const limpo = nome.trim()
  if (limpo === '') return { ok: false, erro: MSG_FORMA_VAZIA }
  if (formasNaTela(ativas, extras).some((f) => chave(f.nome) === chave(limpo))) return { ok: false, erro: MSG_FORMA_REPETIDA }
  return { ok: true, formasAtivas: [...ativas, limpo], formasExtras: [...extras, limpo] }
}

export function excluirFormaExtra(
  ativas: readonly string[], extras: readonly string[], nome: string,
): { formasAtivas: string[]; formasExtras: string[] } {
  const n = chave(nome)
  return { formasAtivas: ativas.filter((a) => chave(a) !== n), formasExtras: extras.filter((e) => chave(e) !== n) }
}

export function formasAtivasDaFornecedora(
  r: Pick<Representada, 'formas_pagamento_aceitas' | 'formas_pagamento_extras'> | undefined,
): string[] {
  return formasNaTela(r?.formas_pagamento_aceitas ?? [], r?.formas_pagamento_extras ?? []).filter((f) => f.ativa).map((f) => f.nome)
}

// Escolha dela (27/09, "a"): sem forma ativa a forma e OPCIONAL -- as 39
// fornecedoras de hoje nao tem nenhuma, e nada pode travar na rua no dia em
// que isto subir.
export function formaObrigatoria(r: Pick<Representada, 'formas_pagamento_aceitas'> | undefined): boolean {
  return (r?.formas_pagamento_aceitas ?? []).length > 0
}
